import { EditListing } from '@components/arsenal/playerMarket/EditListing';
import { BuyListing } from '@components/arsenal/playerMarket/BuyListing';
import { useDeleteListingMutation, MarketListing } from '@http';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui/Button';
import { IconUser } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { useAppStore } from '@stores';
import { useState } from 'react';

interface PlayerMarketListingProps {
  listing: MarketListing;
}

export function PlayerMarketListing({ listing }: PlayerMarketListingProps) {
  const [showEdit, setShowEdit] = useState(false);
  const [showBuy, setShowBuy] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'PlayerMarket.Listing' });
  const { t: tItems } = useTranslation(undefined, { keyPrefix: 'Items' });
  const { mutateAsync: deleteAsync } = useDeleteListingMutation();
  const { id, isFetching, refetch, setIsFetching } = useAppStore();
  const queryClient = useQueryClient();

  const isOwnListing = listing.user.id === id;

  const deleteListing = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await deleteAsync({ id: listing.id });

    queryClient.invalidateQueries({ queryKey: ['tradingListings'] });
    refetch();
  };

  return (
    <>
      <Modal onRequestClose={() => setShowEdit(false)} title={t('editModal')} isOpen={showEdit}>
        <EditListing onClose={() => setShowEdit(false)} listing={listing} />
      </Modal>

      <Modal onRequestClose={() => setShowBuy(false)} title={t('buyModal')} isOpen={showBuy}>
        <BuyListing onClose={() => setShowBuy(false)} listing={listing} />
      </Modal>

      <div className="bg-black border-b border-secondary">
        <div className="flex">
          <div className="w-16 h-16 p-4 relative border-r border-secondary">
            <div className="absolute inset-0 grid-overlay" />
            <img
              src={`/assets/items/${listing.key}.avif`}
              className="w-full h-full object-contain"
              alt={listing.key}
            />
          </div>

          <div className="flex-1 flex flex-col justify-center ml-2">
            <p>{listing.amount}x {tItems(listing.key)}</p>
            <div className="flex w-full mt-2">
              <div className="flex-1 flex items-center">
                <img src="/assets/items/credit.avif" className="h-4 mr-1.5" alt="credit" />
                <p className="text-mono text-sm text-gold leading-none">{listing.price}</p>
              </div>
              <div className="flex-1 flex items-center text-gray-400">
                <IconUser className="h-4 mr-1" />
                <p className="text-mono text-sm leading-none">{listing.user.username}</p>
              </div>
            </div>
          </div>
        </div>

        {isOwnListing && (
          <div className="flex">
            <Button className="flex-1 border-0 border-t border-r" onClick={() => setShowEdit(true)}>
              {t('edit')}
            </Button>
            <Button className="flex-1 btn-danger border-0 border-t border-secondary" onClick={deleteListing}>
              {t('delete')}
            </Button>
          </div>
        )}

        {!isOwnListing && (
          <Button className="w-full border-0 border-t btn-active" onClick={() => setShowBuy(true)}>
            {t('buy')}
          </Button>
        )}
      </div>
    </>
  );
}